import { getConfig } from '@superbet-group/web.lib.config';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import {
    isAfterDate,
    isBeforeDate,
} from '@superbet-group/web.lib.utilities';
import { remove as _remove } from 'lodash';

import { logger } from '@app/logger';
import OfferEvent from '@core/models/offer/OfferEvent';
import BookieDate from '@core/utils/date/BookieDate';
import { TimeSpan } from '@shared/timeFilter/types';

import { ISportOfferState } from './types';
import { StructType } from '@core/models/struct/Struct';
import { Dispatch } from 'vuex';
import { SseStructure } from '@core/services/offer/_private/sse/types';
import { SuperAdvantageEligibility } from '@superbet-group/web.types';
import { EventCategory } from '@core/models/offer/enums';

const isTest = getConfig().environment.isTest;

if (!isTest) {
    (window as any).befores = [];
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const mergeMatch = (matchBefore: Readonly<OfferEvent>, matchAfter: OfferEvent) => {
    // this logic covers next scenarios:
    // 1. we click on tomorrow, and tomorrow has event with same id
    if (matchBefore.incrementId === matchAfter.incrementId) {
        if (matchBefore.containsFullOddList() && matchAfter.containsFullOddList()) {
            return Object.freeze(matchAfter);
        }
        if (matchBefore.containsFullOddList()) {
            if (!isTest) {
                (window as any).befores.push(matchBefore.id);
            }
            return matchBefore;
        }
        if (matchAfter.containsFullOddList()) {
            return Object.freeze(matchAfter);
        } else {
            const beforeOdds = matchBefore.getOdds();
            const afterOdds = matchAfter.getOdds();
            _remove(beforeOdds, (o1) => afterOdds.find((o2) => o1.id === o2.id));
            matchAfter.setOdds([...afterOdds, ...beforeOdds]);
            return Object.freeze(matchAfter);
        }
    }
    if (matchAfter.incrementId > matchBefore.incrementId!) {
        // this logic covers next scenarios:
        // 1. event is changed and comes for prematch only before detail comes
        //    we take everything from before detail and only replace stuff with data came with after
        // 2. we click on tomorrow, and tomorrow has event with same id
        if (matchBefore.containsFullOddList() && !matchAfter.containsFullOddList()) {
            const beforeOdds = matchBefore.getOdds();
            const afterOdds = matchAfter.getOdds();
            _remove(beforeOdds, (o1) => afterOdds.find((o2) => o1.id === o2.id));
            // This covers the case when the new market returns no odds for the main market.
            if (!matchAfter.getPrematchMarket()) {
                _remove(beforeOdds, (odd) => odd.isMain);
            }
            matchAfter.setOdds([...afterOdds, ...beforeOdds]);
            // eslint-disable-next-line no-param-reassign
            matchAfter.oddCount = matchAfter.getOdds().length;
        }
        return Object.freeze(matchAfter);
    }

    // This logic covers the race condition when we fetch an event with full odds
    // but in the meantime sse updates the in memory event with preselected odds.
    // So we are making sure we get the full odds list while keeping the preselected odds.
    if (
        matchBefore.incrementId > matchAfter.incrementId
        && !matchBefore.containsFullOddList() && matchAfter.containsFullOddList()
    ) {
        const beforeOdds = matchBefore.getOdds();
        const afterOdds = matchAfter.getOdds();
        _remove(afterOdds, (o1) => beforeOdds.find((o2) => o1.id === o2.id));
        matchAfter.setOdds([...afterOdds, ...beforeOdds]);

        return Object.freeze(matchAfter);
    }

    // TODO: Remove after we finish with investigation
    // https://superbet.slack.com/archives/CUUM8JLLS/p1650627614264609
    logMergeMatchErrorToSentry(matchBefore, matchAfter);

    return matchBefore;
};

/**
 * Queries the application state for `Match` objects corresponding to the provided IDs.
 *
 * @param ids The match IDs to fetch from the sport offer state.
 * @param state The sport offer state
 * @returns `Match` objects representing matches with the given IDs.
 */
export const eventsById = (ids: number[], state: ISportOfferState) => ids
    .filter((id) => id in state.events)
    .map((id) => state.events[id]);

/**
 * Checks the validity of provided Super Offer events based on the Super Offer's id.
 *
 * @param id The ID string of the Super Offer the events belong to.
 * @param superOfferEvents The Super Offer's events.
 * @returns `true` if the events are acceptable for the given super offer, false otherwise.
 */
export function isSuperOfferValid(id: string, superOfferEvents: OfferEvent[]) {
    return superOfferEvents.length > 0;
}

/**
 * Builds a predicate with a bounded `primaryMarketAttribute` value, useful when filtering.
 *
 * @param primaryMarketAttribute The primaryMarketAttribute string to bind to the predicate.
 * @returns A predicate which takes a `Match` object as its only argument. The
 * predicate returns `true` if the provided `Match` has an unlocked prematch market
 * listed under the bounded `primaryMarketAttribute`, `false` otherwise.
 */
export function isMarketAvailable(primaryMarketAttribute: string) {
    return (event: OfferEvent) => !event.isLocked && event.getPrematchMarket(primaryMarketAttribute)?.isLocked === false;
}

// TODO: Remove after we finish with investigation
// https://superbet.slack.com/archives/CUUM8JLLS/p1650627614264609
async function logMergeMatchErrorToSentry(
    matchBefore: Readonly<OfferEvent>,
    matchAfter: OfferEvent,
) {
    const isSendToSentryEnabled = await featureFlagService.getFeatureValue('merge-match-send-to-sentry', false);
    if (
        isSendToSentryEnabled
        && matchBefore.incrementId > matchAfter.incrementId
        && !matchBefore.containsFullOddList()
        && matchAfter.containsFullOddList()
    ) {
        logger.error(new Error(`Invalid incrementId:
            matchAfterId: ${matchAfter.id},
            matchAfterIncrementId: ${matchAfter.incrementId},
            matchBeforeId: ${matchBefore.id},
            matchBeforeIncrementId: ${matchBefore.incrementId},
        `));
    }
}

export function getStartAndEndDate(timeSpan?: TimeSpan): TimeSpan {
    if (timeSpan) {
        return timeSpan;
    } else {
        return {
            startDate: BookieDate.getStartDateForDate(),
            endDate: BookieDate.getEndDateForDate(),
        };
    }
}

export function findExpiredEventIds(lastEventIds: number[], newEventIds: number[]): number[] {
    const newEventIdsSet = new Set(newEventIds);
    return lastEventIds.filter((id) => !newEventIdsSet.has(id));
}

const sseUpdateActionMapper: Record<keyof SseStructure, string> = {
    [StructType.Sports]: 'updateSports',
    [StructType.Categories]: 'updateCategories',
    [StructType.Tournaments]: 'updateTournaments',
    [StructType.Markets]: 'updateMarkets',
    [StructType.OddTypes]: 'updateOddTypes',
    [StructType.MarketGroupInfos]: 'updateMarketGroupInfos',
};

export function dispatchOfferStructUpdates(struct: Partial<SseStructure>, dispatch: Dispatch): void {
    Object.keys(struct).forEach((structType) => {
        const action = sseUpdateActionMapper[structType as keyof SseStructure];
        Object.values(struct[structType as keyof SseStructure]!).forEach((item) => {
            dispatch(action, item);
        });
    });
}

export function isEventSuperAdvantagePageEligible(event: OfferEvent): boolean {
    const eligibility = event.superAdvantageEligibility;

    if (event.eventCategory === EventCategory.Outright) {
        return false;
    }

    const { startDate, endDate } = BookieDate.getSuperAdvantageDateRange();

    if (isBeforeDate(event.date, startDate) || isAfterDate(event.date, endDate)) {
        return false;
    }

    return event.isOngoing()
        ? eligibility === SuperAdvantageEligibility.All
        : eligibility != null;
}
